import { Grid } from '@material-ui/core';
import { useScrollButtonState } from '@ncr-retail/hooks';
import React, { SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import BaseButton from 'src/common/components/base-button';
import { ITender } from 'src/common/interfaces/dtos/ITender';
import { CashOfficeMenuState } from 'src/common/interfaces/states/CashOfficeMenuState';
import { Pagination } from 'src/pagination/pagination';
import DenominationalTenderCount from '../denominational-tender-count';
import NonDenominationalTenderCount from '../non-denominational-tender-count';
import TenderCountTabContainer from '../tender-count-tab-container';
import { TenderCountState } from '../tender-count/tender-count';
import { styles } from './styles';

export interface CashOfficeTenderingScreenProps {
  vatScreen: boolean;
  setVatScreen: React.Dispatch<SetStateAction<boolean>>;
  setTotalCountAmount: React.Dispatch<SetStateAction<number>>;
  totalCountAmount: number;
  onCancel: () => void;
  isProfileTaxable: boolean;
  submitTenderCount: (
    tenderGroups: CashOfficeMenuState,
    taxAmount?: number,
    taxType?: string,
  ) => void;
  submitButtonText: string;
  setSelectedTenderGroup: (group: string) => void;
}

type cashOfficeProp = CashOfficeTenderingScreenProps & TenderCountState;
export const CashOfficeTenderingScreen = (prop: cashOfficeProp): JSX.Element | null => {
  const {
    tenderMenu,
    leftHanded,
    vatScreen,
    totalCountAmount,
    onCancel,
    eodActivity,
    isProfileTaxable,
    setVatScreen,
    submitTenderCount,
    submitButtonText,
    setSelectedTenderGroup,
    setTotalCountAmount,
  } = prop;

  const classes = styles({ leftHanded });
  const [
    { currentPage, lastPage, onPageChanged, navScrollDownEnabled, navScrollUpEnabled },
    scrollRef,
  ] = useScrollButtonState();
  const { t } = useTranslation();

  const openTenderPickupView = (index: number): void => {
    const tenderGroup = tenderMenu.sellableTenders.tenderGroups[index];
    if (tenderGroup.name) {
      setSelectedTenderGroup(tenderGroup.name);
    }
  };

  const isDenominationTender = (tender: ITender): boolean => {
    return !!(
      tender &&
      tender.isByDenomination &&
      tender.denominations &&
      tender.denominations.denomination.length > 0
    );
  };

  const createTenderCount = (): JSX.Element => {
    const tenderName = tenderMenu.selectedTenderGroup?.name;
    const tenderGroup = tenderMenu.selectedTenderGroup;
    const tenderSelected = tenderMenu.selectedTender;
    if (tenderSelected && tenderGroup) {
      if (isDenominationTender(tenderSelected)) {
        return (
          <DenominationalTenderCount
            titleText={tenderName}
            subtitleText={tenderName}
            onTotalUpdated={onTenderTotalUpdated}
            tenderGroup={tenderGroup}
            selectedTender={tenderSelected}
            denominationGroups={getTenderDenominationGroups(tenderSelected)}
            leftHanded={leftHanded}
          />
        );
      } else {
        return (
          <NonDenominationalTenderCount
            titleText={tenderName}
            subtitleText={tenderName}
            onTotalUpdated={onTenderTotalUpdated}
            tenderGroup={tenderGroup}
            selectedTender={tenderSelected}
            leftHanded={leftHanded}
          />
        );
      }
    }
    return <></>;
  };

  const getTenderDenominationGroups = (tender: ITender): string[] => {
    return Array.from(
      new Set(
        tender.denominations?.denomination.map(i => {
          return i.type.toLowerCase();
        }),
      ),
    );
  };

  const onTenderTotalUpdated = (updatedTenderAmount: number): void => {
    const updatedTotal = totalCountAmount + updatedTenderAmount;
    setTotalCountAmount(updatedTotal);
  };

  if (vatScreen) {
    return null;
  }
  return (
    <Grid
      container
      data-testid='tender-pickup-content-container'
      direction={leftHanded ? 'row-reverse' : 'row'}
      className={classes.tenderCountContent}
    >
      {createTenderCount()}
      <Grid
        item
        xs={3}
        container
        direction='column'
        className={` ${classes.tenderCountControlContainer} fullWidth fullHeight`}
      >
        <div ref={scrollRef} className={`${classes.tenderCountScrollContainer} verticalScroll`}>
          <Grid container data-testid='tender-pickup-tab-container' direction='column'>
            <Grid
              item
              className={classes.tenderGroupTabContainer}
              data-testid='tender-pickup-button-container'
            >
              <TenderCountTabContainer openTenderPickupView={openTenderPickupView} />
            </Grid>
          </Grid>
        </div>
        <Grid container>
          <Grid
            item
            container
            justifyContent='center'
            className={`fullWidth ${classes.tenderCountScrollButtonContainer}`}
          >
            <Grid item container style={{ width: '50%', justifyContent: 'space-evenly' }}>
              <Pagination
                currentPage={currentPage}
                totalPages={lastPage}
                updateCurrentPageHandler={onPageChanged}
                displayText={false}
                testIdPrefix={'cash-office-tendering'}
                canScrollUp={navScrollUpEnabled}
                canScrollDown={navScrollDownEnabled}
              />
            </Grid>
          </Grid>
          <Grid
            item
            container
            data-testid='tender-pickup-button-container'
            className={classes.backgroundWhite}
          >
            <BaseButton
              variant='contained'
              className={`${classes.cancelButton} `}
              centerRipple
              onClick={onCancel}
              disabled={!!(eodActivity && eodActivity.length) || totalCountAmount !== 0}
              data-testid={'cancel-tender-count-button'}
            >
              {t('Cancel_Button')}
            </BaseButton>
            <BaseButton
              color='primary'
              variant='contained'
              className={`${classes.submitTenderCountButton}`}
              centerRipple
              onClick={() => {
                isProfileTaxable ? setVatScreen(true) : submitTenderCount(tenderMenu);
              }}
              disabled={totalCountAmount === 0}
              data-testid={'submit-tender-count-button'}
            >
              {submitButtonText}
            </BaseButton>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
